import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { OpenSheetMusicDisplay } from 'opensheetmusicdisplay';
import CollapsibleSection from './CollapsibleSection';
import { loadSoundfontInstrument } from '../utils/soundfontInstruments';
import { logEarTrainingStart, logEarTrainingComplete, logActivity, ACTIVITY_TYPES } from '../utils/activityLogger';
import * as Tone from 'tone';

const ChordIdentification = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  
  // Game state
  const [gameState, setGameState] = useState('setup'); // 'setup', 'playing', 'finished'
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(10);
  const [currentChord, setCurrentChord] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [roundResults, setRoundResults] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasGuessed, setHasGuessed] = useState(false);
  const [showChord, setShowChord] = useState(false);
  const [notationKey, setNotationKey] = useState(0);
  const [userGuessedChord, setUserGuessedChord] = useState(null);
  const [userGuessedInversion, setUserGuessedInversion] = useState(null);
  const [originalPlaybackType, setOriginalPlaybackType] = useState(null);
  
  // A/B mode state
  const [isInABMode, setIsInABMode] = useState(false);
  const [playingSource, setPlayingSource] = useState(null); // 'A', 'B', or null
  const [guessedChordForAB, setGuessedChordForAB] = useState(null);
  const [loadedInstrument, setLoadedInstrument] = useState(null);
  const [currentRandomInstrument, setCurrentRandomInstrument] = useState(null);
  const [isLoadingInstrument, setIsLoadingInstrument] = useState(false);
  
  // Configuration
  const [config, setConfig] = useState({
    mode: 'mixed', // 'harmonic', 'melodic', 'mixed'
    questionCount: 10,
    enabledChords: new Set(['major', 'minor', 'diminished', 'augmented']),
    currentPlaybackType: 'harmonic',
    melodicDirection: 'ascending', // 'ascending', 'descending', 'both'
    instrument: 'piano',
    enabledOctaves: new Set([3, 4]),
    voicing: 'close', // 'close', 'open', 'both'
    enabledInversions: new Set(['root']), // 'root', '1st', '2nd', '3rd', '4th'
    requireInversionGuessing: false, // When true, player must identify both chord type AND inversion
    volume: 0 // Default to full volume (0dB)
  });
  
  const audioContextRef = useRef(null);
  const osmdContainerRef = useRef(null);
  const osmdRef = useRef(null);
  const externalOsmdContainer = useRef(null);

  // Comprehensive chord definitions
  const chordTypes = [
    // Basic Triads
    { id: 'major', name: 'Major', intervals: [0, 4, 7], symbol: '', category: 'triads' },
    { id: 'minor', name: 'Minor', intervals: [0, 3, 7], symbol: 'm', category: 'triads' },
    { id: 'diminished', name: 'Diminished', intervals: [0, 3, 6], symbol: '°', category: 'triads' },
    { id: 'augmented', name: 'Augmented', intervals: [0, 4, 8], symbol: '+', category: 'triads' },
    
    // Suspended Chords
    { id: 'sus2', name: 'Suspended 2nd', intervals: [0, 2, 7], symbol: 'sus2', category: 'suspended' },
    { id: 'sus4', name: 'Suspended 4th', intervals: [0, 5, 7], symbol: 'sus4', category: 'suspended' },
    
    // Seventh Chords
    { id: 'major7', name: 'Major 7th', intervals: [0, 4, 7, 11], symbol: 'maj7', category: 'sevenths' },
    { id: 'minor7', name: 'Minor 7th', intervals: [0, 3, 7, 10], symbol: 'm7', category: 'sevenths' },
    { id: 'dominant7', name: 'Dominant 7th', intervals: [0, 4, 7, 10], symbol: '7', category: 'sevenths' },
    { id: 'diminished7', name: 'Diminished 7th', intervals: [0, 3, 6, 9], symbol: '°7', category: 'sevenths' },
    { id: 'halfDiminished7', name: 'Half Diminished 7th', intervals: [0, 3, 6, 10], symbol: 'ø7', category: 'sevenths' },
    { id: 'minorMajor7', name: 'Minor Major 7th', intervals: [0, 3, 7, 11], symbol: 'm(maj7)', category: 'sevenths' },
    { id: 'augmented7', name: 'Augmented 7th', intervals: [0, 4, 8, 10], symbol: '+7', category: 'sevenths' },
    
    // Extended Chords
    { id: 'major9', name: 'Major 9th', intervals: [0, 4, 7, 11, 14], symbol: 'maj9', category: 'extended' },
    { id: 'minor9', name: 'Minor 9th', intervals: [0, 3, 7, 10, 14], symbol: 'm9', category: 'extended' },
    { id: 'dominant9', name: 'Dominant 9th', intervals: [0, 4, 7, 10, 14], symbol: '9', category: 'extended' },
    { id: 'major11', name: 'Major 11th', intervals: [0, 4, 7, 11, 14, 17], symbol: 'maj11', category: 'extended' },
    { id: 'minor11', name: 'Minor 11th', intervals: [0, 3, 7, 10, 14, 17], symbol: 'm11', category: 'extended' },
    { id: 'dominant11', name: 'Dominant 11th', intervals: [0, 4, 7, 10, 14, 17], symbol: '11', category: 'extended' },
    { id: 'major13', name: 'Major 13th', intervals: [0, 4, 7, 11, 14, 21], symbol: 'maj13', category: 'extended' },
    { id: 'minor13', name: 'Minor 13th', intervals: [0, 3, 7, 10, 14, 21], symbol: 'm13', category: 'extended' },
    { id: 'dominant13', name: 'Dominant 13th', intervals: [0, 4, 7, 10, 14, 21], symbol: '13', category: 'extended' },
    
    // Altered Chords
    { id: 'dominant7flat5', name: 'Dominant 7th ♭5', intervals: [0, 4, 6, 10], symbol: '7♭5', category: 'altered' },
    { id: 'dominant7sharp5', name: 'Dominant 7th ♯5', intervals: [0, 4, 8, 10], symbol: '7♯5', category: 'altered' },
    { id: 'dominant7flat9', name: 'Dominant 7th ♭9', intervals: [0, 4, 7, 10, 13], symbol: '7♭9', category: 'altered' },
    { id: 'dominant7sharp9', name: 'Dominant 7th ♯9', intervals: [0, 4, 7, 10, 15], symbol: '7♯9', category: 'altered' },
    { id: 'dominant7sharp11', name: 'Dominant 7th ♯11', intervals: [0, 4, 7, 10, 18], symbol: '7♯11', category: 'altered' }
  ];

  const instruments = {
    random: {
      name: `${t('earTraining.instruments.random')}` + ' (Random)',
      soundfont: null
    },
    piano: {
      name: `${t('earTraining.instruments.piano')}` + ' (Piano)',
      soundfont: 'piano'
    },
    electric_piano: {
      name: `${t('earTraining.instruments.electricPiano')}` + ' (Electric Piano)',
      soundfont: 'electric_piano'
    },
    guitar: {
      name: `${t('earTraining.instruments.guitar')}` + ' (Guitar)',
      soundfont: 'guitar'
    },
    bass: {
      name: `${t('earTraining.instruments.bass')}` + ' (Bass)',
      soundfont: 'bass'
    },
    strings: {
      name: `${t('earTraining.instruments.strings')}` + ' (Strings)',
      soundfont: 'strings'
    },
    brass: {
      name: `${t('earTraining.instruments.brass')}` + ' (Brass)',
      soundfont: 'brass'
    },
    saxophone: {
      name: `${t('earTraining.instruments.saxophone')}` + ' (Saxophone)',
      soundfont: 'saxophone'
    },
    flute: {
      name: `${t('earTraining.instruments.flute')}` + ' (Flute)',
      soundfont: 'flute'
    }
  };

  // Configuration is disabled during gameplay
  const isConfigDisabled = gameState === 'playing';

  // Proper chord spelling function
  const getProperChordSpelling = (rootNote, chordType, chromaticNotes) => {
    // Define proper chord spellings for common chords
    const chordSpellings = {
      'C': { 
        major: ['C', 'E', 'G'], 
        minor: ['C', 'Eb', 'G'],
        diminished: ['C', 'Eb', 'Gb'],
        augmented: ['C', 'E', 'G#']
      },
      'C#': { 
        major: ['C#', 'E#', 'G#'], 
        minor: ['C#', 'E', 'G#'],
        diminished: ['C#', 'E', 'G'],
        augmented: ['C#', 'E#', 'G##']
      },
      'Db': { 
        major: ['Db', 'F', 'Ab'], 
        minor: ['Db', 'Fb', 'Ab'],
        diminished: ['Db', 'Fb', 'Abb'],
        augmented: ['Db', 'F', 'A']
      },
      'D': { 
        major: ['D', 'F#', 'A'], 
        minor: ['D', 'F', 'A'],
        diminished: ['D', 'F', 'Ab'],
        augmented: ['D', 'F#', 'A#']
      },
      'D#': { 
        major: ['D#', 'F##', 'A#'], 
        minor: ['D#', 'F#', 'A#'],
        diminished: ['D#', 'F#', 'A'],
        augmented: ['D#', 'F##', 'A##']
      },
      'Eb': { 
        major: ['Eb', 'G', 'Bb'], 
        minor: ['Eb', 'Gb', 'Bb'],
        diminished: ['Eb', 'Gb', 'Bbb'],
        augmented: ['Eb', 'G', 'B']
      },
      'E': { 
        major: ['E', 'G#', 'B'], 
        minor: ['E', 'G', 'B'],
        diminished: ['E', 'G', 'Bb'],
        augmented: ['E', 'G#', 'B#']
      },
      'F': { 
        major: ['F', 'A', 'C'], 
        minor: ['F', 'Ab', 'C'],
        diminished: ['F', 'Ab', 'Cb'],
        augmented: ['F', 'A', 'C#']
      },
      'F#': { 
        major: ['F#', 'A#', 'C#'], 
        minor: ['F#', 'A', 'C#'],
        diminished: ['F#', 'A', 'C'],
        augmented: ['F#', 'A#', 'C##']
      },
      'Gb': { 
        major: ['Gb', 'Bb', 'Db'], 
        minor: ['Gb', 'Bbb', 'Db'],
        diminished: ['Gb', 'Bbb', 'Dbb'],
        augmented: ['Gb', 'Bb', 'D']
      },
      'G': { 
        major: ['G', 'B', 'D'], 
        minor: ['G', 'Bb', 'D'],
        diminished: ['G', 'Bb', 'Db'],
        augmented: ['G', 'B', 'D#']
      },
      'G#': { 
        major: ['G#', 'B#', 'D#'], 
        minor: ['G#', 'B', 'D#'],
        diminished: ['G#', 'B', 'D'],
        augmented: ['G#', 'B#', 'D##']
      },
      'Ab': { 
        major: ['Ab', 'C', 'Eb'], 
        minor: ['Ab', 'Cb', 'Eb'],
        diminished: ['Ab', 'Cb', 'Ebb'],
        augmented: ['Ab', 'C', 'E']
      },
      'A': { 
        major: ['A', 'C#', 'E'], 
        minor: ['A', 'C', 'E'],
        diminished: ['A', 'C', 'Eb'],
        augmented: ['A', 'C#', 'E#']
      },
      'A#': { 
        major: ['A#', 'C##', 'E#'], 
        minor: ['A#', 'C#', 'E#'],
        diminished: ['A#', 'C#', 'E'],
        augmented: ['A#', 'C##', 'E##']
      },
      'Bb': { 
        major: ['Bb', 'D', 'F'], 
        minor: ['Bb', 'Db', 'F'],
        diminished: ['Bb', 'Db', 'Fb'],
        augmented: ['Bb', 'D', 'F#']
      },
      'B': { 
        major: ['B', 'D#', 'F#'], 
        minor: ['B', 'D', 'F#'],
        diminished: ['B', 'D', 'F'],
        augmented: ['B', 'D#', 'F##']
      }
    };

    // For triads, use the lookup table
    if (chordType.id === 'major' && chordSpellings[rootNote]) {
      return chordSpellings[rootNote].major;
    } else if (chordType.id === 'minor' && chordSpellings[rootNote]) {
      return chordSpellings[rootNote].minor;
    } else if (chordType.id === 'diminished' && chordSpellings[rootNote]) {
      return chordSpellings[rootNote].diminished;
    } else if (chordType.id === 'augmented' && chordSpellings[rootNote]) {
      return chordSpellings[rootNote].augmented;
    }
    
    // For other chord types, return the chromatic notes as-is
    // This covers extended chords and other complex chords
    return chromaticNotes;
  };

  // Cleanup external DOM container
  useEffect(() => {
    return () => {
      if (externalOsmdContainer.current && externalOsmdContainer.current.parentNode) {
        externalOsmdContainer.current.parentNode.removeChild(externalOsmdContainer.current);
      }
    };
  }, []);

  // Create external container for OSMD outside React's DOM management (like IntervalTraining)
  useEffect(() => {
    if (osmdContainerRef.current && !externalOsmdContainer.current) {
      // Create a div that will hold OSMD content
      externalOsmdContainer.current = document.createElement('div');
      externalOsmdContainer.current.style.width = '100%';
      externalOsmdContainer.current.style.height = '100%';
      osmdContainerRef.current.appendChild(externalOsmdContainer.current);
    }

    return () => {
      if (externalOsmdContainer.current && osmdContainerRef.current) {
        try {
          osmdContainerRef.current.removeChild(externalOsmdContainer.current);
        } catch (e) {
          // Ignore errors during cleanup
        }
        externalOsmdContainer.current = null;
      }
    };
  }, [notationKey, showChord]);

  const generateMusicXML = (rootNote, chordType, inversion = 'root') => {
    const chromaticNotes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const rootIndex = chromaticNotes.indexOf(rootNote);
    
    // Generate chord notes using chromatic intervals first
    let chordNotes = chordType.intervals.map(interval => {
      return chromaticNotes[(rootIndex + interval) % 12];
    });

    // Apply proper chord spelling based on chord type and root
    chordNotes = getProperChordSpelling(rootNote, chordType, chordNotes);

    // Apply inversion
    if (inversion === '1st' && chordNotes.length > 1) {
      chordNotes = [...chordNotes.slice(1), chordNotes[0]];
    } else if (inversion === '2nd' && chordNotes.length > 2) {
      chordNotes = [...chordNotes.slice(2), ...chordNotes.slice(0, 2)];
    } else if (inversion === '3rd' && chordNotes.length > 3) {
      chordNotes = [...chordNotes.slice(3), ...chordNotes.slice(0, 3)];
    }

    // Use proper octaves to keep notes in ascending order
    const noteWithOctaves = [];
    
    for (let index = 0; index < chordNotes.length; index++) {
      const note = chordNotes[index];
      let octave = 4;
      
      if (index === 0) {
        // Root note always in octave 4
        octave = 4;
      } else {
        // For subsequent notes, ensure they're higher than the previous note
        const prevNote = chordNotes[index - 1];
        const prevNoteIndex = chromaticNotes.indexOf(prevNote.charAt(0)) + (prevNote.includes('#') ? 0.5 : prevNote.includes('b') ? -0.5 : 0);
        const currentNoteIndex = chromaticNotes.indexOf(note.charAt(0)) + (note.includes('#') ? 0.5 : note.includes('b') ? -0.5 : 0);
        
        // Start with same octave as previous note
        octave = noteWithOctaves[index - 1].octave;
        
        // If current note is lower in the chromatic scale, move to next octave
        if (currentNoteIndex <= prevNoteIndex) {
          octave += 1;
        }
      }
      
      noteWithOctaves.push({ note, octave });
    }
    

    return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 3.1 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">
<score-partwise version="3.1">
  <part-list>
    <score-part id="P1">
      <part-name></part-name>
    </score-part>
  </part-list>
  <part id="P1">
    <measure number="1">
      <attributes>
        <divisions>1</divisions>
        <key>
          <fifths>0</fifths>
        </key>
        <time>
          <beats>4</beats>
          <beat-type>4</beat-type>
        </time>
        <clef>
          <sign>G</sign>
          <line>2</line>
        </clef>
      </attributes>
      <note>
        <pitch>
          <step>${noteWithOctaves[0].note.charAt(0)}</step>
${noteWithOctaves[0].note.includes('##') ? '<alter>2</alter>' : 
            noteWithOctaves[0].note.includes('bb') ? '<alter>-2</alter>' :
            noteWithOctaves[0].note.includes('#') ? '<alter>1</alter>' : 
            noteWithOctaves[0].note.includes('b') ? '<alter>-1</alter>' : ''}
          <octave>${noteWithOctaves[0].octave}</octave>
        </pitch>
        <duration>4</duration>
        <type>whole</type>
      </note>
      ${noteWithOctaves.slice(1).map(noteObj => `
      <note>
        <chord/>
        <pitch>
          <step>${noteObj.note.charAt(0)}</step>
${noteObj.note.includes('##') ? '<alter>2</alter>' : 
            noteObj.note.includes('bb') ? '<alter>-2</alter>' :
            noteObj.note.includes('#') ? '<alter>1</alter>' : 
            noteObj.note.includes('b') ? '<alter>-1</alter>' : ''}
          <octave>${noteObj.octave}</octave>
        </pitch>
        <duration>4</duration>
        <type>whole</type>
      </note>`).join('')}
    </measure>
  </part>
</score-partwise>`;
  };

  const renderNotation = async () => {
    if (!showChord || !currentChord || !externalOsmdContainer.current) return;

    try {
      if (osmdRef.current) {
        try {
          osmdRef.current.clear();
        } catch (e) {
          // Ignore errors during cleanup
        }
        osmdRef.current = null;
      }

      // Create new OSMD instance using external container (copied from IntervalTraining)
      const osmd = new OpenSheetMusicDisplay(externalOsmdContainer.current, {
        autoResize: true,
        backend: "svg",
        drawTitle: false,
      });

      osmdRef.current = osmd;

      // Set a smaller zoom level to make the notation more compact
      osmd.zoom = 0.6;

      // Generate and load the MusicXML
      const musicXML = generateMusicXML(currentChord.root, currentChord.type, currentChord.inversion);
      
      await osmd.load(musicXML);
      
      osmd.render();

      // Add mobile-friendly zoom
      if (window.innerWidth <= 768) {
        osmd.zoom = 0.5;
        osmd.render();
      } else {
        osmd.render();
      }
    } catch (error) {
      console.error('Error rendering notation:', error);
    }
  };

  // Render notation only AFTER user guesses (when showChord is true) 
  useEffect(() => {
    if (currentChord && showChord) {
      // Use a small delay to ensure DOM is ready (like IntervalTraining)
      const timer = setTimeout(() => {
        // Ensure external container exists first
        if (osmdContainerRef.current && !externalOsmdContainer.current) {
          externalOsmdContainer.current = document.createElement('div');
          externalOsmdContainer.current.style.width = '100%';
          externalOsmdContainer.current.style.height = '100%';
          osmdContainerRef.current.appendChild(externalOsmdContainer.current);
        }
        
        if (externalOsmdContainer.current) {
          renderNotation();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentChord, notationKey, showChord]);

  // Auto-play when new chord is generated (enabled like IntervalTraining)
  useEffect(() => {
    console.log('DEBUG: Auto-play useEffect triggered:', {
      currentChord: currentChord?.symbol,
      gameState,
      hasGuessed,
      loadedInstrument: !!loadedInstrument,
      instrumentType: config.instrument
    });
    
    if (currentChord && gameState === 'playing' && !hasGuessed && loadedInstrument) {
      console.log('DEBUG: All conditions met for auto-play, setting 300ms timer');
      const timer = setTimeout(() => {
        console.log('DEBUG: Auto-play timer fired, calling playChord()');
        playChord();
      }, 300);
      return () => {
        console.log('DEBUG: Cleaning up auto-play timer');
        clearTimeout(timer);
      };
    } else {
      console.log('DEBUG: Auto-play conditions not met:', {
        hasCurrentChord: !!currentChord,
        isPlaying: gameState === 'playing',
        notGuessed: !hasGuessed,
        hasInstrument: !!loadedInstrument
      });
    }
  }, [currentChord, gameState, hasGuessed, loadedInstrument]);

  // Load instrument when configuration changes during gameplay
  useEffect(() => {
    if (gameState === 'playing') {
      loadInstrument(config.instrument);
    }
  }, [config.instrument, config.volume]);

  const loadInstrument = async (instrumentKey) => {
    console.log('DEBUG: Loading instrument:', instrumentKey);
    setIsLoadingInstrument(true);
    
    if (instrumentKey === 'random') {
      const availableInstruments = Object.keys(instruments).filter(key => key !== 'random');
      const randomInstrument = availableInstruments[Math.floor(Math.random() * availableInstruments.length)];
      console.log('DEBUG: Random instrument selected:', randomInstrument);
      setCurrentRandomInstrument(randomInstrument);
      instrumentKey = randomInstrument;
    }

    const instrument = instruments[instrumentKey];
    if (!instrument || !instrument.soundfont) {
      console.log('DEBUG: Invalid instrument or missing soundfont:', instrumentKey);
      setIsLoadingInstrument(false);
      return null;
    }

    try {
      console.log('DEBUG: Loading soundfont:', instrument.soundfont, '(this may take a few seconds...)');
      const loadedInstr = await loadSoundfontInstrument(instrument.soundfont, config.volume);
      console.log('DEBUG: Instrument loaded successfully:', instrumentKey);
      setLoadedInstrument(loadedInstr);
      setIsLoadingInstrument(false);
      return loadedInstr;
    } catch (error) {
      console.error('DEBUG: Error loading instrument:', error);
      setIsLoadingInstrument(false);
      return null;
    }
  };

  const getNoteFrequency = (noteName, octave) => {
    const noteToSemitone = {
      'C': 0, 'C#': 1, 'D': 2, 'D#': 3, 'E': 4, 'F': 5,
      'F#': 6, 'G': 7, 'G#': 8, 'A': 9, 'A#': 10, 'B': 11
    };
    
    const semitone = noteToSemitone[noteName];
    if (semitone === undefined) return null;
    
    return 440 * Math.pow(2, (semitone - 9 + (octave - 4) * 12) / 12);
  };

  const playChord = async (type = null) => {
    // When type is null, preserve the original playback type from when the chord was first played
    const playbackType = type || originalPlaybackType || currentChord?.playbackType || 'harmonic';
    
    console.log('DEBUG: playChord called with conditions:', {
      hasCurrentChord: !!currentChord,
      chordSymbol: currentChord?.symbol,
      explicitType: type,
      currentPlaybackType: currentChord?.playbackType,
      storedOriginalPlaybackType: originalPlaybackType,
      finalPlaybackType: playbackType,
      direction: currentChord?.direction,
      isPlaying,
      hasLoadedInstrument: !!loadedInstrument
    });
    
    if (!currentChord || isPlaying || !loadedInstrument) {
      console.log('DEBUG: playChord conditions not met, exiting early');
      return;
    }

    console.log('DEBUG: Starting chord playback...', playbackType);
    setIsPlaying(true);

    try {
      if (Tone.context.state !== 'running') {
        console.log('DEBUG: Starting Tone.js context');
        await Tone.start();
      }

      const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
      const rootIndex = notes.indexOf(currentChord.root);
      
      let chordNotes = currentChord.type.intervals.map(interval => {
        return notes[(rootIndex + interval) % 12];
      });

      // Apply inversion
      if (currentChord.inversion === '1st' && chordNotes.length > 1) {
        chordNotes = [...chordNotes.slice(1), chordNotes[0]];
      } else if (currentChord.inversion === '2nd' && chordNotes.length > 2) {
        chordNotes = [...chordNotes.slice(2), ...chordNotes.slice(0, 2)];
      } else if (currentChord.inversion === '3rd' && chordNotes.length > 3) {
        chordNotes = [...chordNotes.slice(3), ...chordNotes.slice(0, 3)];
      }

      // Use the consistent octave assigned to this chord
      const baseOctave = currentChord.baseOctave;

      // Proper octave assignment for chords to avoid unintended inversions
      const noteWithOctave = chordNotes.map((note, index) => {
        let octave = baseOctave;
        
        // For chords in proper voicing, ensure notes don't cross below the bass note
        if (index > 0) {
          const chromaticOrder = ['C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'F', 'F#', 'Gb', 'G', 'G#', 'Ab', 'A', 'A#', 'Bb', 'B'];
          const bassNoteIndex = chromaticOrder.indexOf(chordNotes[0]);
          const currentNoteIndex = chromaticOrder.indexOf(note);
          
          // If current note is lower in chromatic order than bass note, move it up an octave
          if (currentNoteIndex < bassNoteIndex) {
            octave += 1;
          }
        }
        
        return `${note}${octave}`;
      });

      console.log('DEBUG: Playing chord notes:', noteWithOctave, 'from chord:', currentChord.symbol, 'octave:', currentChord.baseOctave, 'type:', playbackType);
      console.log('DEBUG: About to choose playback path. playbackType === "harmonic"?', playbackType === 'harmonic');
      console.log('DEBUG: playbackType type:', typeof playbackType, 'value:', playbackType);

      if (playbackType === 'harmonic') {
        // Play all notes simultaneously (original behavior)
        const now = Tone.now();
        noteWithOctave.forEach(note => {
          loadedInstrument.triggerAttackRelease(note, '2n', now);
        });
        console.log('DEBUG: Harmonic chord playback started');

        setTimeout(() => {
          console.log('DEBUG: Harmonic chord playback finished');
          setIsPlaying(false);
        }, 1000);

      } else {
        // Play notes sequentially (melodic) - respecting the original direction
        const noteDuration = '0.7n';
        const noteGap = 0.5; // seconds between notes
        
        // For melodic playback, sort notes by pitch but preserve inversion structure
        // The inversion has already been applied to chordNotes, so we sort the octave-assigned notes
        const sortedNotes = [...noteWithOctave].sort((a, b) => {
          const noteA = a.slice(0, -1); // Note name without octave
          const octaveA = parseInt(a.slice(-1)); // Octave number
          const noteB = b.slice(0, -1);
          const octaveB = parseInt(b.slice(-1));
          
          // Compare octaves first, then note pitch within octave
          if (octaveA !== octaveB) {
            return octaveA - octaveB;
          }
          
          // Within same octave, compare semitone positions
          const semitones = { 'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3, 'E': 4, 'F': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8, 'Ab': 8, 'A': 9, 'A#': 10, 'Bb': 10, 'B': 11 };
          return (semitones[noteA] || 0) - (semitones[noteB] || 0);
        });
        
        let playOrder = [...sortedNotes];
        if (currentChord.direction === 'descending') {
          playOrder = playOrder.reverse();
          console.log('DEBUG: Playing melodic chord descending (sorted):', playOrder);
        } else {
          console.log('DEBUG: Playing melodic chord ascending (sorted):', playOrder);
        }

        playOrder.forEach((note, index) => {
          const startTime = Tone.now() + (index * noteGap);
          loadedInstrument.triggerAttackRelease(note, noteDuration, startTime);
        });

        const totalDuration = (playOrder.length * noteGap + 0.5) * 1000; // Convert to ms
        setTimeout(() => {
          console.log('DEBUG: Melodic chord playback finished');
          setIsPlaying(false);
        }, totalDuration);
      }

    } catch (error) {
      console.error('DEBUG: Error playing chord:', error);
      setIsPlaying(false);
    }
  };

  const arpeggiateChord = async () => {
    // Force arpeggiated (melodic ascending) playback regardless of original type
    // Temporarily override the chord direction to ensure ascending arpeggiation
    const originalDirection = currentChord.direction;
    currentChord.direction = 'ascending';
    await playChord('melodic');
    currentChord.direction = originalDirection; // Restore original direction
  };

  const createGuessedChord = (guessedChordTypeId, guessedInversionId = null) => {
    if (!currentChord || !guessedChordTypeId) return null;
    
    // Find the guessed chord type
    const guessedChordType = chordTypes.find(chord => chord.id === guessedChordTypeId);
    if (!guessedChordType) return null;
    
    // Use the guessed inversion or default to the current chord's inversion
    const finalInversion = guessedInversionId || currentChord.inversion;
    
    // Create the guessed chord with same root, octave, playback settings as the correct answer
    // but with the user's guessed chord type and inversion
    const guessedChord = {
      type: guessedChordType,
      root: currentChord.root,
      inversion: finalInversion,
      baseOctave: currentChord.baseOctave,
      playbackType: currentChord.playbackType,
      originalPlaybackType: currentChord.originalPlaybackType,
      direction: currentChord.direction,
      symbol: `${currentChord.root}${guessedChordType.symbol}`
    };
    
    return guessedChord;
  };

  // A/B Mode Control Functions
  const handleABModeToggle = () => {
    if (!userGuessedChord) return;
    
    // Create the guessed chord for A/B comparison
    const guessedChord = createGuessedChord(
      userGuessedChord.id, 
      userGuessedInversion
    );
    
    setGuessedChordForAB(guessedChord);
    setIsInABMode(true);
    setPlayingSource(null);
  };

  const handleExitABMode = () => {
    setIsInABMode(false);
    setPlayingSource(null);
    setGuessedChordForAB(null);
    // Return to feedback state instead of proceeding to next question
  };

  const playCorrectChord = async () => {
    if (isPlaying || !currentChord) return;
    
    setPlayingSource('A');
    try {
      await playSpecificChord(currentChord);
    } finally {
      setPlayingSource(null);
    }
  };

  const playGuessedChord = async () => {
    if (isPlaying || !guessedChordForAB) return;
    
    setPlayingSource('B');
    try {
      await playSpecificChord(guessedChordForAB);
    } finally {
      setPlayingSource(null);
    }
  };

  const playSpecificChord = async (chordToPlay) => {
    if (!chordToPlay || !loadedInstrument) return;

    setIsPlaying(true);

    try {
      if (Tone.context.state !== 'running') {
        await Tone.start();
      }

      const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
      const rootIndex = notes.indexOf(chordToPlay.root);
      
      let chordNotes = chordToPlay.type.intervals.map(interval => {
        return notes[(rootIndex + interval) % 12];
      });

      // Apply inversion
      if (chordToPlay.inversion === '1st' && chordNotes.length > 1) {
        chordNotes = [...chordNotes.slice(1), chordNotes[0]];
      } else if (chordToPlay.inversion === '2nd' && chordNotes.length > 2) {
        chordNotes = [...chordNotes.slice(2), ...chordNotes.slice(0, 2)];
      } else if (chordToPlay.inversion === '3rd' && chordNotes.length > 3) {
        chordNotes = [...chordNotes.slice(3), ...chordNotes.slice(0, 3)];
      }

      const baseOctave = chordToPlay.baseOctave;
      const noteWithOctave = chordNotes.map((note, index) => {
        let octave = baseOctave;
        
        if (index > 0) {
          const chromaticOrder = ['C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'F', 'F#', 'Gb', 'G', 'G#', 'Ab', 'A', 'A#', 'Bb', 'B'];
          const bassNoteIndex = chromaticOrder.indexOf(chordNotes[0]);
          const currentNoteIndex = chromaticOrder.indexOf(note);
          
          if (currentNoteIndex < bassNoteIndex) {
            octave += 1;
          }
        }
        
        return `${note}${octave}`;
      });

      const playbackType = chordToPlay.originalPlaybackType || chordToPlay.playbackType || 'harmonic';

      if (playbackType === 'harmonic') {
        const now = Tone.now();
        noteWithOctave.forEach(note => {
          loadedInstrument.triggerAttackRelease(note, '2n', now);
        });

        setTimeout(() => {
          setIsPlaying(false);
        }, 1000);
      } else {
        const noteDuration = '0.7n';
        const noteGap = 0.5;
        
        const sortedNotes = [...noteWithOctave].sort((a, b) => {
          const noteA = a.slice(0, -1);
          const octaveA = parseInt(a.slice(-1));
          const noteB = b.slice(0, -1);
          const octaveB = parseInt(b.slice(-1));
          
          if (octaveA !== octaveB) {
            return octaveA - octaveB;
          }
          
          const semitones = { 'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3, 'E': 4, 'F': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8, 'Ab': 8, 'A': 9, 'A#': 10, 'Bb': 10, 'B': 11 };
          return (semitones[noteA] || 0) - (semitones[noteB] || 0);
        });
        
        let playOrder = [...sortedNotes];
        if (chordToPlay.direction === 'descending') {
          playOrder = playOrder.reverse();
        }

        playOrder.forEach((note, index) => {
          const startTime = Tone.now() + (index * noteGap);
          loadedInstrument.triggerAttackRelease(note, noteDuration, startTime);
        });

        const totalDuration = (playOrder.length * noteGap + 0.5) * 1000;
        setTimeout(() => {
          setIsPlaying(false);
        }, totalDuration);
      }
    } catch (error) {
      console.error('Error playing specific chord:', error);
      setIsPlaying(false);
    }
  };

  const generateNewChord = () => {
    const enabledChordIds = Array.from(config.enabledChords);
    const availableChords = chordTypes.filter(chord => enabledChordIds.includes(chord.id));
    
    if (availableChords.length === 0) return null;

    const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const inversions = Array.from(config.enabledInversions);
    const octaves = Array.from(config.enabledOctaves);
    
    const randomChordType = availableChords[Math.floor(Math.random() * availableChords.length)];
    const randomRoot = notes[Math.floor(Math.random() * notes.length)];
    const randomInversion = inversions[Math.floor(Math.random() * inversions.length)];
    const randomOctave = octaves[Math.floor(Math.random() * octaves.length)];

    // Determine playback type for this question (like IntervalTraining)
    let playbackType;
    if (config.mode === 'mixed') {
      playbackType = Math.random() < 0.5 ? 'harmonic' : 'melodic';
    } else {
      playbackType = config.mode;
    }

    // Determine direction for melodic chords (applies to both melodic mode and mixed mode)
    let direction = 'ascending'; // default
    if (playbackType === 'melodic' || config.mode === 'mixed') {
      if (config.melodicDirection === 'both') {
        direction = Math.random() < 0.5 ? 'ascending' : 'descending';
      } else {
        direction = config.melodicDirection;
      }
    }

    const chord = {
      type: randomChordType,
      root: randomRoot,
      inversion: randomInversion,
      baseOctave: randomOctave,
      playbackType,
      originalPlaybackType: playbackType, // Store the original for Play Again functionality
      direction,
      symbol: randomRoot + randomChordType.symbol
    };
    
    console.log('DEBUG: Generated new chord:', chord);
    return chord;
  };

  const startGame = async () => {
    console.log('DEBUG: Starting game with config:', {
      enabledChords: Array.from(config.enabledChords),
      instrument: config.instrument,
      questionCount: config.questionCount
    });
    
    if (config.enabledChords.size === 0) {
      console.log('DEBUG: No chords enabled, aborting game start');
      return;
    }

    // Load instrument FIRST before changing any game state
    console.log('DEBUG: Loading instrument before starting game...');
    await loadInstrument(config.instrument);
    console.log('DEBUG: Instrument loading completed');
    
    // Generate first chord
    console.log('DEBUG: Generating first chord...');
    const newChord = generateNewChord();
    if (!newChord) {
      console.log('DEBUG: Failed to generate chord');
      return;
    }

    // Now set all game state at once to minimize re-renders
    console.log('DEBUG: Setting all game state...');
    setGameState('playing');
    setCurrentQuestion(0);
    setScore({ correct: 0, total: 0 });
    setRoundResults([]);
    setTotalQuestions(config.questionCount);
    setCurrentChord(newChord);
    setOriginalPlaybackType(newChord.playbackType); // Store the original playback type
    setHasGuessed(false);
    setFeedback(null);
    setShowChord(false);
    setUserGuessedChord(null);
    setUserGuessedInversion(null);
    
    // Log training session start
    await logEarTrainingStart(user, 'Chord Identification', config);
    setNotationKey(prev => prev + 1);
    console.log('DEBUG: Game setup complete, auto-play should trigger for:', newChord.symbol);
  };

  const startNewQuestion = async () => {
    console.log('DEBUG: Starting new question...');
    const newChord = generateNewChord();
    if (!newChord) {
      console.log('DEBUG: Failed to generate new chord');
      return;
    }

    console.log('DEBUG: Setting new chord:', newChord.symbol);
    setCurrentChord(newChord);
    setOriginalPlaybackType(newChord.playbackType); // Store the original playback type
    setHasGuessed(false);
    setFeedback(null);
    setShowChord(false);
    setUserGuessedChord(null);
    setUserGuessedInversion(null);
    setNotationKey(prev => prev + 1);
    console.log('DEBUG: New question setup complete, auto-play should trigger via useEffect');
  };

  const handleChordGuess = (guessedChordId) => {
    if (hasGuessed || !currentChord) return;

    const guessedChord = chordTypes.find(c => c.id === guessedChordId);
    setUserGuessedChord(guessedChord);

    // If inversion guessing is not required, complete the guess
    if (!config.requireInversionGuessing) {
      completeGuess(guessedChord, null);
    }
    // If inversion guessing is required, wait for inversion selection
  };

  const handleInversionGuess = (guessedInversionId) => {
    if (hasGuessed || !currentChord || !userGuessedChord) return;

    setUserGuessedInversion(guessedInversionId);
    completeGuess(userGuessedChord, guessedInversionId);
  };

  const completeGuess = (guessedChord, guessedInversionId) => {
    let isCorrect = guessedChord.id === currentChord.type.id;
    
    // If inversion guessing is required, both chord and inversion must be correct
    if (config.requireInversionGuessing) {
      const inversionCorrect = guessedInversionId === currentChord.inversion;
      isCorrect = isCorrect && inversionCorrect;
    }
    
    setHasGuessed(true);
    setShowChord(true);
    
    const newScore = {
      correct: score.correct + (isCorrect ? 1 : 0),
      total: score.total + 1
    };
    setScore(newScore);

    const result = {
      question: currentQuestion + 1,
      chord: currentChord,
      userGuess: guessedChord,
      userGuessedInversion: guessedInversionId,
      correct: isCorrect
    };
    setRoundResults(prev => [...prev, result]);

    // Create feedback message
    let feedbackMessage = isCorrect ? 
      t('earTraining.feedback.correct') : 
      t('earTraining.feedback.incorrect');
    
    let actualAnswer = currentChord.type.name;
    let userAnswer = guessedChord.name;
    
    if (config.requireInversionGuessing) {
      const inversionNames = {
        'root': t('earTraining.inversions.root'),
        '1st': t('earTraining.inversions.first'),
        '2nd': t('earTraining.inversions.second'),
        '3rd': t('earTraining.inversions.third')
      };
      actualAnswer += ` (${inversionNames[currentChord.inversion] || currentChord.inversion})`;
      userAnswer += ` (${inversionNames[guessedInversionId] || guessedInversionId})`;
    }

    setFeedback({
      isCorrect,
      message: feedbackMessage,
      actualChord: actualAnswer,
      userGuess: userAnswer
    });
  };

  const nextQuestion = async () => {
    if (currentQuestion + 1 >= totalQuestions) {
      setGameState('finished');
      
      // Log training session completion
      await logEarTrainingComplete(user, 'Chord Identification', {
        correct: score.correct,
        total: score.total,
        roundResults
      });
    } else {
      setCurrentQuestion(prev => prev + 1);
      startNewQuestion();
    }
  };

  const resetGame = () => {
    setGameState('setup');
    setCurrentQuestion(0);
    setCurrentChord(null);
    setScore({ correct: 0, total: 0 });
    setRoundResults([]);
    setFeedback(null);
    setHasGuessed(false);
    setShowChord(false);
    setUserGuessedChord(null);
    setUserGuessedInversion(null);
  };

  const renderConfigArea = () => (
    <div className={`interval-config ${isConfigDisabled ? 'disabled' : ''}`}>
      <div className="config-header">
        <h4>{t('earTraining.activities.chordIdentification.config.title')}</h4>
      </div>

      <div className="config-section">
        <label>{t('earTraining.config.questionCount')}</label>
        <select 
          value={config.questionCount} 
          onChange={(e) => setConfig(prev => ({...prev, questionCount: parseInt(e.target.value)}))}
          disabled={isConfigDisabled}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={15}>15</option>
          <option value={20}>20</option>
          <option value={25}>25</option>
        </select>
      </div>

      <div className="config-section">
        <label>{t('earTraining.config.instrument')}</label>
        <select 
          value={config.instrument} 
          onChange={(e) => setConfig(prev => ({...prev, instrument: e.target.value}))}
        >
          {Object.entries(instruments).map(([key, instrument]) => (
            <option key={key} value={key}>{instrument.name}</option>
          ))}
        </select>
      </div>

      <div className="config-section">
        <label>{t('earTraining.config.volume')}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input 
            type="range" 
            min="-40" 
            max="6" 
            value={config.volume}
            step="1"
            onChange={(e) => setConfig(prev => ({...prev, volume: Number(e.target.value)}))}
            style={{ flex: 1 }}
          />
          <span className="volume-value" style={{ minWidth: '50px', fontSize: '12px' }}>
            {config.volume} dB
          </span>
        </div>
      </div>

      <div className="config-section">
        <label>{t('earTraining.config.playbackMode')}</label>
        <div className="mode-buttons">
          {['harmonic', 'melodic', 'mixed'].map((mode) => (
            <button
              key={mode}
              className={`mode-btn ${config.mode === mode ? 'active' : ''}`}
              onClick={() => setConfig(prev => ({ ...prev, mode }))}
              disabled={isConfigDisabled}
            >
              {mode === 'harmonic' ? `${t('earTraining.modes.harmonic')}` : 
               mode === 'melodic' ? `${t('earTraining.modes.melodic')}` : 
               `${t('earTraining.modes.mixed')}`}
            </button>
          ))}
        </div>
      </div>

      {(config.mode === 'melodic' || config.mode === 'mixed') && (
        <div className="config-section">
          <label>{t('earTraining.config.melodicDirection')}</label>
          <div className="mode-buttons">
            {[
              { value: 'ascending', label: `${t('earTraining.directions.ascending')}` },
              { value: 'descending', label: `${t('earTraining.directions.descending')}` },
              { value: 'both', label: `${t('earTraining.directions.both')}` }
            ].map((direction) => (
              <button
                key={direction.value}
                className={`mode-btn ${config.melodicDirection === direction.value ? 'active' : ''}`}
                onClick={() => setConfig(prev => ({ ...prev, melodicDirection: direction.value }))}
                disabled={isConfigDisabled}
              >
                {direction.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="config-section">
        <label>{t('earTraining.activities.chordIdentification.config.chordTypes')}</label>
        <div className="interval-checkboxes">
          {chordTypes.map(chord => (
            <div key={chord.id} className="interval-checkbox">
              <input
                type="checkbox"
                id={`chord-${chord.id}`}
                checked={config.enabledChords.has(chord.id)}
                onChange={(e) => {
                  const newSet = new Set(config.enabledChords);
                  if (e.target.checked) {
                    newSet.add(chord.id);
                  } else {
                    newSet.delete(chord.id);
                  }
                  setConfig(prev => ({...prev, enabledChords: newSet}));
                }}
                disabled={isConfigDisabled}
              />
              <span>{chord.name}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="config-section">
        <label>{t('earTraining.activities.chordIdentification.config.inversions')}</label>
        <div className="mode-buttons">
          {[
            { id: 'root', name: t('earTraining.activities.chordIdentification.inversions.root') },
            { id: '1st', name: t('earTraining.activities.chordIdentification.inversions.first') },
            { id: '2nd', name: t('earTraining.activities.chordIdentification.inversions.second') }
          ].map(inversion => (
            <button
              key={inversion.id}
              className={`mode-btn ${config.enabledInversions.has(inversion.id) ? 'active' : ''}`}
              onClick={() => {
                const newSet = new Set(config.enabledInversions);
                if (newSet.has(inversion.id)) {
                  if (newSet.size > 1) newSet.delete(inversion.id);
                } else {
                  newSet.add(inversion.id);
                }
                setConfig(prev => ({...prev, enabledInversions: newSet}));
              }}
              disabled={isConfigDisabled}
            >
              {inversion.name}
            </button>
          ))}
        </div>
      </div>

      <div className="config-section">
        <label>
          <input
            type="checkbox"
            checked={config.requireInversionGuessing}
            onChange={(e) => setConfig(prev => ({...prev, requireInversionGuessing: e.target.checked}))}
            disabled={isConfigDisabled}
          />
          {t('earTraining.activities.chordIdentification.config.requireInversionGuessing')}
        </label>
      </div>

      <button 
        className="start-game-btn" 
        onClick={startGame}
        disabled={config.enabledChords.size === 0 || isConfigDisabled || isLoadingInstrument}
      >
        {isLoadingInstrument ? 
          t('earTraining.buttons.loadingInstrument') : 
          (gameState === 'setup' ? t('earTraining.buttons.startTraining') : t('earTraining.buttons.newRound'))
        }
      </button>
    </div>
  );

  const renderPlayArea = () => {
    if (gameState === 'setup') {
      return (
        <div className="play-area setup">
          <div className="setup-message">
            <h3>{t('earTraining.activities.chordIdentification.title')}</h3>
            <p>{t('earTraining.activities.chordIdentification.description')}</p>
            <button 
              className="start-game-btn" 
              onClick={startGame}
              disabled={config.enabledChords.size === 0 || isLoadingInstrument}
            >
              {isLoadingInstrument ? 
                t('earTraining.buttons.loadingInstrument') : 
                t('earTraining.buttons.startTraining')
              }
            </button>
          </div>
        </div>
      );
    }

    if (gameState === 'finished') {
      const percentage = Math.round((score.correct / score.total) * 100);
      return (
        <div className="play-area finished">
          <div className="results-summary">
            <h3>{t('earTraining.results.roundComplete') || 'Round Complete!'}</h3>
            <div className="final-score">
              <div className="score-circle">
                <div className="score-percentage">{percentage}%</div>
                <div className="score-fraction">{score.correct}/{score.total}</div>
              </div>
            </div>
            
            <div className="results-breakdown">
              {roundResults.map((result, index) => (
                <div key={index} className={`result-item ${result.correct ? 'correct' : 'incorrect'}`}>
                  <span className="question-num">#{result.question}</span>
                  <span className="interval-name">{result.chord.symbol}</span>
                  <span className="result-icon">{result.correct ? 'PASS' : 'FAIL'}</span>
                </div>
              ))}
            </div>

            <div className="result-actions">
              <button 
                className="play-again-btn" 
                onClick={startGame}
                disabled={isLoadingInstrument}
              >
                {isLoadingInstrument ? 
                  t('earTraining.buttons.loadingInstrument') : 
                  t('earTraining.buttons.playAgain')
                }
              </button>
              <button className="setup-btn" onClick={resetGame}>
                {t('earTraining.buttons.backToSetup')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Playing state
    return (
      <div className="play-area playing">
        <div className="game-header">
          <div className="progress">
            {t('earTraining.progress', { current: currentQuestion + 1, total: totalQuestions })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="current-score" style={{
              background: '#1e293b',
              color: '#f1f5f9',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #475569'
            }}>
              <span className="score-label" style={{color: '#94a3b8', fontSize: '12px', marginRight: '8px'}}>{t('earTraining.score')}:</span>
              <span className="score-value" style={{color: '#60a5fa', fontWeight: 'bold', fontSize: '16px'}}>{score.correct}/{score.total}</span>
            </div>
            <button 
              onClick={resetGame}
              style={{
                background: '#dc2626',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.background = '#b91c1c'}
              onMouseOut={(e) => e.target.style.background = '#dc2626'}
              title={t('earTraining.gameplay.quitAndReturn')}
            >
              {t('earTraining.gameplay.quit')}
            </button>
          </div>
        </div>

        <div className="chord-display-container">
          {!showChord ? (
            <div className="pre-guess-display">
              <div className="guess-prompt">
                <div className="listen-icon">[Listen]</div>
                <h3>{t('earTraining.activities.chordIdentification.listenAndGuess')}</h3>
                <div className="wave-animation">
                  <span>♪</span>
                  <span>♫</span>
                  <span>♪</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="chord-reveal-container">
              <div className="chord-symbol-display">
                <div className="chord-symbol-large">{currentChord?.symbol}</div>
                <div className="chord-type-name">{currentChord?.type.name}</div>
                {currentChord?.inversion !== 'root' && (
                  <div className="chord-inversion">{currentChord.inversion} inversion</div>
                )}
              </div>
              <div className="chord-notation-container">
                <div className="notation-header">
                  <span className="notation-label">Staff Notation</span>
                </div>
                <div 
                  key={notationKey}
                  ref={osmdContainerRef} 
                  className="chord-notation"
                  style={{
                    backgroundColor: 'white',
                    color: 'black',
                    filter: 'none',
                    position: 'relative',
                    minHeight: '90px',
                    maxHeight: '110px',
                    padding: '4px'
                  }}
                >
                  {/* This will be replaced by OSMD content */}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="play-controls">
          <button 
            className="play-interval-btn" 
            onClick={() => playChord()}
            disabled={isPlaying}
          >
            {isPlaying ? t('earTraining.buttons.playing') : t('earTraining.buttons.playChord')}
          </button>
        </div>

        <div className="feedback-area">
          {isInABMode ? (
            <div style={{
              margin: '8px 0',
              padding: '12px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: 'white',
              boxShadow: '0 4px 16px rgba(30, 41, 59, 0.3)'
            }}>
              <div style={{marginBottom: '12px', fontSize: '14px', fontWeight: '600', textAlign: 'center', textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'}}>
                🎵 {t('earTraining.gameplay.abComparisonMode')}
              </div>
              
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px'}}>
                <div style={{textAlign: 'center', padding: '8px', background: '#1f2937', borderRadius: '6px', border: '1px solid #16a34a', borderLeft: '4px solid #16a34a'}}>
                  <div style={{fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#86efac'}}>
                    A: {t('earTraining.gameplay.correctAnswer')}
                  </div>
                  <div style={{fontSize: '12px', marginBottom: '6px', color: '#d1d5db'}}>
                    {currentChord?.symbol} - {currentChord?.type.name}
                    {currentChord?.inversion !== 'root' && (
                      <div style={{fontSize: '11px', fontStyle: 'italic', color: '#9ca3af'}}>
                        {currentChord.inversion} inv.
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={playCorrectChord}
                    disabled={isPlaying}
                    style={{
                      background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                      color: '#86efac',
                      border: '1px solid #16a34a',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}
                  >
                    {playingSource === 'A' ? t('earTraining.buttons.playing') : `🔊 ${t('earTraining.gameplay.playA')}`}
                  </button>
                </div>
                
                <div style={{textAlign: 'center', padding: '8px', background: '#1f2937', borderRadius: '6px', border: '1px solid #dc2626', borderLeft: '4px solid #dc2626'}}>
                  <div style={{fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#fca5a5'}}>
                    B: {t('earTraining.gameplay.yourGuess')}
                  </div>
                  <div style={{fontSize: '12px', marginBottom: '6px', color: '#d1d5db'}}>
                    {guessedChordForAB?.symbol} - {guessedChordForAB?.type.name}
                    {guessedChordForAB?.inversion !== 'root' && (
                      <div style={{fontSize: '11px', fontStyle: 'italic', color: '#9ca3af'}}>
                        {guessedChordForAB.inversion} inv.
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={playGuessedChord}
                    disabled={isPlaying}
                    style={{
                      background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                      color: '#fca5a5',
                      border: '1px solid #dc2626',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}
                  >
                    {playingSource === 'B' ? t('earTraining.buttons.playing') : `🔊 ${t('earTraining.gameplay.playB')}`}
                  </button>
                </div>
              </div>
              
              <div style={{display: 'flex', justifyContent: 'space-between', gap: '10px'}}>
                <button 
                  onClick={handleExitABMode}
                  style={{
                    background: 'linear-gradient(135deg, #475569 0%, #64748b 100%)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  ← Back
                </button>
                <button 
                  onClick={() => {
                    setIsInABMode(false);
                    setPlayingSource(null);
                    setGuessedChordForAB(null);
                    nextQuestion();
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #475569 0%, #64748b 100%)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  {currentQuestion + 1 >= totalQuestions ? 
                    t('earTraining.buttons.finishRound') : 
                    t('earTraining.buttons.nextQuestion')
                  } →
                </button>
              </div>
            </div>
          ) : feedback ? (
            <div className={`inline-feedback ${feedback.isCorrect ? 'feedback-correct' : 'feedback-incorrect'}`}>
              <div className="feedback-content">
                <span className="feedback-icon">{feedback.isCorrect ? 'CORRECT' : 'INCORRECT'}</span>
                <span className="feedback-result">{feedback.message}</span>
                {!feedback.isCorrect && (
                  <div className="feedback-answer">
                    {t('earTraining.feedback.correctAnswer')}: {feedback.actualChord}
                  </div>
                )}
              </div>
              <div className="post-guess-actions">
                <button className="action-btn secondary" onClick={() => {
                  console.log('DEBUG: Play Again clicked. Current chord object:', currentChord);
                  playChord();
                }} disabled={isPlaying}>
                  {t('earTraining.buttons.playAgain')}
                </button>
                {currentChord?.playbackType === 'harmonic' && (
                  <button className="action-btn secondary" onClick={arpeggiateChord} disabled={isPlaying}>
                    {t('earTraining.buttons.arpeggiate')}
                  </button>
                )}
                {!feedback.isCorrect && userGuessedChord && !isInABMode && (
                  <button className="action-btn secondary" onClick={handleABModeToggle} disabled={isPlaying}>
                    {t('earTraining.gameplay.abItForMe')}
                  </button>
                )}
                <button className="action-btn primary" onClick={nextQuestion}>
                  {currentQuestion + 1 >= totalQuestions ? 
                    t('earTraining.buttons.finishRound') : 
                    t('earTraining.buttons.nextQuestion')
                  }
                </button>
              </div>
            </div>
          ) : (
            <div className="chord-choices">
              {!config.requireInversionGuessing ? (
                // Simple chord guessing - one click to complete
                <div className="chord-guessing-section">
                  <div className="guess-prompt">
                    <h3 className="guess-title">{t('earTraining.activities.chordIdentification.guessPrompt')}</h3>
                  </div>
                  <div className="interval-choices">
                    {chordTypes
                      .filter(chord => config.enabledChords.has(chord.id))
                      .map(chord => (
                        <button
                          key={chord.id}
                          className="interval-choice chord-choice"
                          onClick={() => handleChordGuess(chord.id)}
                          disabled={hasGuessed}
                        >
                          {chord.name}
                        </button>
                      ))
                    }
                  </div>
                </div>
              ) : (
                // Two-step guessing: chord type first, then inversion
                <>
                  {!userGuessedChord ? (
                    // Step 1: Choose chord type
                    <div className="chord-guessing-section">
                      <div className="guess-prompt">
                        <h3 className="guess-title">{t('earTraining.activities.chordIdentification.guessChordType')}</h3>
                      </div>
                      <div className="interval-choices">
                        {chordTypes
                          .filter(chord => config.enabledChords.has(chord.id))
                          .map(chord => (
                            <button
                              key={chord.id}
                              className="interval-choice chord-choice"
                              onClick={() => handleChordGuess(chord.id)}
                              disabled={hasGuessed}
                            >
                              {chord.name}
                            </button>
                          ))
                        }
                      </div>
                    </div>
                  ) : (
                    // Step 2: Choose inversion
                    <div className="chord-guessing-section">
                      <div className="guess-prompt">
                        <h3 className="guess-title">
                          {t('earTraining.activities.chordIdentification.guessInversion')}
                        </h3>
                        <div className="chord-context">
                          {userGuessedChord.name}
                        </div>
                      </div>
                      <div className="interval-choices">
                        {['root', '1st', '2nd', '3rd']
                          .filter(inv => config.enabledInversions.has(inv))
                          .map(inversion => (
                            <button
                              key={inversion}
                              className="interval-choice inversion-choice"
                              onClick={() => handleInversionGuess(inversion)}
                              disabled={hasGuessed}
                            >
                              {inversion === 'root' ? t('earTraining.inversions.root') :
                               inversion === '1st' ? t('earTraining.inversions.first') :
                               inversion === '2nd' ? t('earTraining.inversions.second') :
                               t('earTraining.inversions.third')}
                            </button>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="interval-training-container">
      <div className="desktop-layout">
        <div className="config-area-desktop">
          {renderConfigArea()}
        </div>
        
        <div className="play-area-container">
          <div className="config-section-mobile">
            <CollapsibleSection title={t('earTraining.config.title')} defaultExpanded={false}>
              {renderConfigArea()}
            </CollapsibleSection>
          </div>
          
          {renderPlayArea()}
        </div>
      </div>
    </div>
  );
};

export default ChordIdentification;